import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { AccessLevel, CatalogSelectOption, ExistingAccessLevel, ShareUser, TranslateFn } from "../types";
import { getUserEmail, getUserInitial, getUserName, toTestId } from "../utils/catalog";

type SharedUsersListProps = {
  t: TranslateFn;
  users: ShareUser[];
  loading: boolean;
  error?: boolean;
  currentUserId?: string;
  accessLevels: CatalogSelectOption<AccessLevel>[];
  updatingShare: Record<string, boolean>;
  onAccessChange: (user: ShareUser, accessLevel: ExistingAccessLevel) => void;
  onRetry?: () => void;
};

const SharedUsersList = ({ t, users, loading, error = false, currentUserId, accessLevels, updatingShare, onAccessChange, onRetry }: SharedUsersListProps) => {
  if (loading) return <div className="flex items-center justify-center py-8"><Spinner className="size-6" /></div>;
  if (error) return (
    <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
      <span>{t("aiCatalog.shareDialog.failedToLoadSharedUsers")}</span>
      {onRetry ? <Button type="button" variant="outline" size="sm" onClick={onRetry}>{t("common.retry")}</Button> : null}
    </div>
  );
  if (!users.length) return <p className="text-sm text-muted-foreground">{t("aiCatalog.shareDialog.notSharedYet")}</p>;
  return (
    <div className="divide-y divide-border">
      {users.map((user) => {
        const email = getUserEmail(user);
        return (
          <div key={user.id || email} className="flex min-h-[53px] min-w-0 items-center justify-between gap-4 py-2">
            <div className="flex min-w-0 items-center gap-3">
              <span aria-hidden="true" className="flex size-5 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] text-secondary-foreground">{getUserInitial(user, t("aiCatalog.shareDialog.unknownUser"))}</span>
              <div className="min-w-0">
                <p className="truncate text-sm text-foreground">{getUserName(user, t("aiCatalog.shareDialog.unknownUser"))}</p>
                <p className="truncate text-sm text-muted-foreground" dir="ltr">{email}</p>
              </div>
            </div>
            {user.user_id === currentUserId ? null : (
              <Select value={user.access_level || user.role || "viewer"} onValueChange={(value) => onAccessChange(user, value as ExistingAccessLevel)} disabled={!!updatingShare[email]}>
                <SelectTrigger data-testid={`ai-catalog-share-existing-access-${toTestId(email)}`} aria-label={`${t("aiCatalog.shareDialog.permission")} ${email}`} className="h-9 min-w-30 bg-muted">
                  {updatingShare[email] ? <Spinner className="size-4" /> : <SelectValue />}
                </SelectTrigger>
                <SelectContent>
                  {accessLevels.map((option) => <SelectItem data-testid={`ai-catalog-share-existing-access-option-${toTestId(email)}-${option.value}`} key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  <SelectItem data-testid={`ai-catalog-share-existing-access-option-${toTestId(email)}-remove`} value="remove">{t("common.remove")}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SharedUsersList;
